import { DOCUMENT, inject, Injectable } from '@angular/core';
import { StateService } from './state.service';
import { environment } from '../../environments/environment';
import { ScriptService } from './script.service';
import {
  BehaviorSubject,
  catchError,
  filter,
  fromEvent,
  map,
  noop,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';

import { IS_SERVER } from '../providers/is-platform.provider';
import { SeoService } from './seo.service';
import { CookiebotService } from './cookiebot.service';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { Cart, SyneriseCart } from '../pages/cart-page/cart.types';
import { Gender } from '../pages/customer/customer.types';
import { TranslocoService } from '@jsverse/transloco';
import { ProductListPage } from '../pages/product/product-list/product-list.types';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { RecommendationSlide } from '../components/carousel/recommendation-carousel/recommendations.type';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare const SR: any;

const SYNERISE_INIT_EVENT_NAME = 'syneriseInitialised';
const SYNERISE_SCRIPT_ID = 'synerise';

@Injectable({
  providedIn: 'root',
})
export class SyneriseService {
  private sr: any = {
    event: {
      trackCustomEvent: noop,
      sendFormData: noop,
    },
  };
  private readonly state = inject(StateService);
  private readonly script = inject(ScriptService);
  private readonly document = inject(DOCUMENT);
  private readonly isEnabled = environment.isSyneriseEnabled;
  private readonly isServer = inject(IS_SERVER);
  private readonly seo = inject(SeoService);
  private readonly cookiebot = inject(CookiebotService);
  private readonly cookies = inject(SsrCookieService);
  private readonly t = inject(TranslocoService);
  private readonly http = inject(HttpClient);
  private readonly _initialized = new BehaviorSubject<boolean>(false);
  readonly initialized = toSignal(this._initialized);

  init(): void {
    if (!this.isEnabled || this.isServer) {
      return;
    }

    this.cookiebot.consents$.subscribe(({ statistics }) => this.toggle(statistics));

    this.cookiebot.consents$.pipe(take(1)).subscribe(() => {
      const key = environment.syneriseKeys.get(this.state.getActiveLocale());

      if (!key) {
        return;
      }

      this.script.createAndAppend({
        body: this.createScriptBody(key),
        id: SYNERISE_SCRIPT_ID,
      });

      // this won't work if one day we decide not to reload the whole page when user changes locale
      // take(1) is not the problem here - the problem is that synerise lacks the option to
      // re-initialize with a different tracking code
      fromEvent(this.document, SYNERISE_INIT_EVENT_NAME)
        .pipe(take(1))
        .subscribe(() => {
          this.sr = SR;
          this._initialized.next(true);
          this.seo.ogTagsChanged$.subscribe(this.onPageVisit);
        });
    });
  }

  updateCartStatus(cart?: Cart): void {
    const emptyCart = {
      products: [],
      totalAmount: 0,
      totalQuantity: 0,
    };

    this.sr.event.trackCustomEvent(
      'cart.status',
      cart ? this.mapCartToSynerise(cart) : emptyCart,
      'CartStatus',
    );
  }

  onAddToFavorite(sku: string): void {
    this.sr.event.trackCustomEvent('product.addToFavorite', { sku: sku });
  }

  onRemoveFromFavorite(sku: string): void {
    this.sr.event.trackCustomEvent('product.removeFromFavourite', { sku: sku });
  }

  onSendFormData(from: string, data: SyneriseData): void {
    this.sr.event.sendFormData(from, data);
  }

  getListingRecommendations(
    campaignId: string,
    page: ProductListPage | undefined | null,
  ): Observable<RecommendationSlide[]> {
    if (!page?.products?.length || !('client' in this.sr)) {
      return of([]);
    }

    return this._initialized.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => {
        const productIds = page.products.map((p) => p.productId);
        const categoryFilter = `attributes.simple_type == "${page.meta.category.name}"`;
        const payload = {
          clientUUID: this.sr.client.getCurrentClient().uuid,
          campaignId: campaignId,
          additionalFilters: categoryFilter,
          filtersJoiner: 'AND',
          itemsExcluded: productIds,
          displayAttributes: ['sku'],
          includeContextItems: false,
        };

        return this.http
          .post<{ data: RecommendationSlide[] }>(
            'https://api.synerise.com/recommendations/v2/recommend/campaigns',
            payload,
            {
              headers: {
                'X-Api-Key': this.sr.auth.trackerKey(),
              },
            },
          )
          .pipe(
            map(({ data }) => data),
            catchError(() => of([])),
          );
      }),
    );
  }

  mapToSyneriseData(
    formValue: Partial<{
      email: string;
      firstName: string;
      lastName: string;
      password: string;
      confirmPassword: string;
    }>,
    optionals: { birthday: Date; gender: Gender },
  ): SyneriseData {
    const { email, firstName, lastName } = formValue;
    const { gender, birthday } = optionals;

    return {
      email,
      firstname: firstName,
      lastname: lastName,
      ...(gender && { sex: this.t.translate(gender).toString().toLowerCase() }),
      ...(birthday && { birthdate: birthday.toISOString().split('T')[0] }),
    };
  }

  private toggle(isTrackingEnabled: boolean): void {
    this.setDntCookie(!isTrackingEnabled);
  }

  private setDntCookie(value: boolean): void {
    const now = new Date();
    const expires = new Date(now.setFullYear(now.getFullYear() + 1));
    this.cookies.set('_snrs_dnt', value.toString(), {
      expires,
      path: '/',
      secure: true,
      sameSite: 'None',
    });
  }

  private onPageVisit = (): void => {
    if ('dynamicContent' in this.sr) {
      this.sr.event.pageVisit().then(() => {
        this.sr.dynamicContent.get();
      });
    }
  };

  private createScriptBody(trackingKey: string): string {
    return `
  function onSyneriseLoad() {
    if (SR && typeof SR.init === 'function') {
      SR.init({
          trackerKey: "${trackingKey}",
          customPageVisit: true,
          dynamicContent: {
            virtualPage: true
          },
          disableWebPush: true,
          dataLayer: dataLayer,
        });
      document.dispatchEvent(new CustomEvent('${SYNERISE_INIT_EVENT_NAME}'));
    }
  }
  (function(s,y,n,e,r,i,se){s["SyneriseObjectNamespace"]=r;s[r]=s[r]||[],
   s[r]._t=1*new Date(),s[r]._i=0,s[r]._l=i;var z=y.createElement(n),
   se=y.getElementsByTagName(n)[0];z.async=1;z.src=e;se.parentNode.insertBefore(z,se);
   z.onload=z.onreadystatechange=function(){var rdy=z.readyState;
   if(!rdy||/complete|loaded/.test(z.readyState)){s[i]();z.onload = null;
   z.onreadystatechange=null;}};})(window,document,"script", "//web.snrbox.com/synerise-javascript-sdk.min.js", "SR", "onSyneriseLoad");
`;
  }

  private mapCartToSynerise(cart: Cart | undefined): SyneriseCart {
    if (!cart || !cart.lineItems) {
      return {
        products: [],
        totalAmount: 0,
        totalQuantity: 0,
        itemIds: [],
      };
    }

    const products = cart.lineItems.map((item: any) => ({
      sku: item.sku,
      quantity: item.quantity,
      category:
        item.categories && item.categories.length > 0
          ? item.categories[0].prettyName
          : 'unknown',
    }));

    const totalAmount = cart.totalAmount || 0;

    const totalQuantity = cart.itemCount;

    const itemIds = cart.lineItems.map((item: any) => item.sku);

    return {
      products,
      totalAmount,
      totalQuantity,
      itemIds,
    };
  }
}

export type SyneriseData = {
  email?: string;
  firstname?: string;
  lastname?: string;
  city?: string;
  phone?: string;
  zipCode?: string;
  sex?: string;
  birthdate?: string;
  newsletterAgreement?: 'enabled' | 'disabled';
  receive_smses?: '1' | '0';
};
